import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, ChangeDetectionStrategy } from '@angular/core';
import { IconComponent } from '../../icons/icon.component';

@Component({
  selector: 'app-pagination',
  standalone: true,
  imports: [CommonModule, IconComponent],
  templateUrl: './pagination.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./pagination.component.scss'],
})
export class PaginationComponent {
  @Input() currentPage = 1;
  @Input() totalItems = 0;
  @Input() pageSize = 5;
  /** Fallback page count when `totalItems` is not available (server-side). */
  @Input() pageCount?: number | null;
  /** Rows on the current page (server-side); refines the range on the last page. */
  @Input() currentPageItemCount?: number | null;
  @Input() pageSizes: number[] = [5, 10, 15, 20];
  @Output() pageChange = new EventEmitter<number>();
  @Output() pageSizeChange = new EventEmitter<number>();
  @Input() paddingXAxis = 'px-0 sm:px-1 md:px-2';
  @Input() paddingYAxis = 'py-3';
  @Input() forceStack = false;

  get totalPages(): number {
    if (this.totalItems > 0 && this.pageSize > 0) {
      return Math.ceil(this.totalItems / this.pageSize);
    }
    if (this.pageCount != null && Number.isFinite(this.pageCount) && this.pageCount >= 0) {
      return this.pageCount;
    }
    return 0;
  }

  get totalPagesArray(): number[] {
    return Array.from({ length: this.totalPages }, (_, i) => i + 1);
  }

  /** Total shown in "of N items" — uses API `count` via `totalItems`. */
  get displayTotalItems(): number {
    return this.totalItems;
  }

  get startItem(): number {
    return this.displayTotalItems ? (this.currentPage - 1) * this.pageSize + 1 : 0;
  }

  get endItem(): number {
    if (!this.displayTotalItems) return 0;
    if (
      this.currentPage === this.totalPages &&
      this.currentPageItemCount !== null &&
      this.currentPageItemCount !== undefined
    ) {
      const rowsOnPage = Number(this.currentPageItemCount);
      if (Number.isFinite(rowsOnPage) && rowsOnPage >= 0) {
        return this.startItem + rowsOnPage - 1;
      }
    }
    return Math.min(this.currentPage * this.pageSize, this.displayTotalItems);
  }
  getDisplayedPages(): (number | string)[] {
    const total = this.totalPages;
    const current = this.currentPage;
    const maxVisible = 5; // show 5 pages at once
    const pages: (number | string)[] = [];

    if (total <= maxVisible) {
      // if total pages are less than or equal to 5, show all
      for (let i = 1; i <= total; i++) pages.push(i);
    } else {
      // calculate group start & end based on current page
      const group = Math.ceil(current / maxVisible);
      const start = (group - 1) * maxVisible + 1;
      const end = Math.min(group * maxVisible, total);

      // previous ellipsis if not in first group
      if (start > 1) pages.push('...');

      // add pages in current group
      for (let i = start; i <= end; i++) pages.push(i);

      // next ellipsis if not in last group
      if (end < total) pages.push('...');
    }

    return pages;
  }

  changePage(page: any) {
    if (page >= 1 && page <= this.totalPages) {
      this.pageChange.emit(page);
    }
  }

  changePageSize(size: number) {
    this.pageSizeChange.emit(size);
  }
}
